/* eslint-disable sonarjs/cognitive-complexity */
import { Box, Select, TextField, Typography } from '@astro-ui/components'
import theme from '@astro-ui/theme'

import { useAppDispatch } from 'store'
import DatePicker from 'components/DatePicker'
import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
  OutboundSOAddEditSliceType,
  setAllFormValues,
} from 'features/SupplyOrder/redux/outboundSOAddEdit'
import { withPopupConfirmation } from 'components/PopupConfirmation'
import useBasicDetail from './useBasicDetail'

type FieldsetPropsType = {
  label: string
  fullWidth?: boolean
  required?: boolean
  children: React.ReactNode
}

const Fieldset = ({ label, fullWidth = false, required = false, children }: FieldsetPropsType) => (
  <Box display="flex" alignItems="center">
    <Box minWidth={210}>
      <Typography variant="body-small" color="#646464">
        {label} {required && <span style={{ color: theme.palette.error.main }}>*</span>}
      </Typography>
    </Box>
    <Box width={fullWidth ? '100%' : 300}>{children}</Box>
  </Box>
)

const BasicDetail = () => {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)

  const hasInitEdit = useRef(false)

  const dispatch = useAppDispatch()
  const { constants, data, form, errorField, handleChangeForm, handleSearchPOReff } =
    useBasicDetail()
  const isReturnType = form.soType.toLowerCase() === 'return'

  useEffect(() => {
    if (!isEdit) return
    if (!data) return
    if (!constants.locations.length || !constants.remarks.length) return
    if (data.supplyOrderType?.toLowerCase() === 'return' && !constants.poRefferences.length) return
    if (hasInitEdit.current) return

    hasInitEdit.current = true

    const { locations, poRefferences, remarks: remarkList } = constants

    if (data) {
      const soType = data.supplyOrderType || ''
      const soReff = data.supplyOrderReff || ''

      let origin = null
      let destination = null

      const email = data.supplyOrderRequestBy || ''
      const remarks = remarkList.find((rem) => rem.code === data.supplyOrderRemarks) || null
      const soDate = data.supplyOrderDate ? new Date(data.supplyOrderDate) : null
      const shippingInstructions = data.supplyOrderShippingInstructions || ''

      const poReff =
        data.supplyOrderType?.toLowerCase() === 'return'
          ? poRefferences.find((po) => po.poNumber === soReff) || undefined
          : undefined

      origin = locations.find((loc) => loc.location_id === data.locationOriginId) || null
      destination = locations.find((loc) => loc.location_id === data.locationDestinationId) || null

      const newFormState: OutboundSOAddEditSliceType['form'] = {
        soType,
        soReff,
        poReff,
        origin,
        destination,
        email,
        remarks,
        soDate,
        shippingInstructions,
      }

      dispatch(setAllFormValues(newFormState))
    }
  }, [constants, data, dispatch, isEdit, isReturnType])

  return (
    <Box
      id="detail-dasar"
      borderRadius="8px"
      border="1px solid"
      borderColor={theme.colors.border}
      backgroundColor={theme.colors.white}
      padding="24px"
      overflow="auto"
    >
      <Typography variant="display-tiny" marginBottom="16px">
        Detail Dasar
      </Typography>

      <Box display="flex" flexDirection="column" gap="16px" mt="32px">
        <Fieldset label="SO Type" required>
          <Select
            required
            fullWidth
            disabled={isEdit}
            placeholder="Pilih tipe"
            value={form.soType}
            options={constants.type}
            onChange={(value) => handleChangeForm('soType', value)}
          />
        </Fieldset>

        {isReturnType ? (
          <Fieldset label="PO Reff" required>
            <Select
              required
              withSearch
              fullWidth
              placeholder="Masukan Referensi PO"
              displayKey="poNumber"
              value={form.poReff || null}
              options={constants.poRefferences}
              onChange={(value) => handleChangeForm('poReff', value)}
              onSearch={handleSearchPOReff}
            />
          </Fieldset>
        ) : (
          <Fieldset label="SO Reff">
            <TextField
              fullWidth
              placeholder="Masukan Referensi SO"
              value={form.soReff}
              onChange={(e) => handleChangeForm('soReff', e.target.value)}
            />
          </Fieldset>
        )}

        <Fieldset label="Asal" required>
          <Select
            required
            fullWidth
            withSearch
            disabled={isEdit}
            placeholder="Cari lokasi"
            displayKey="location_name"
            value={form.origin}
            options={constants.origins}
            onChange={(value) => handleChangeForm('origin', value)}
          />
        </Fieldset>

        <Fieldset label="Tujuan" required>
          {isReturnType ? (
            <Select
              required
              fullWidth
              withSearch
              placeholder="Cari lokasi"
              displayKey="location_name"
              disabled={isReturnType}
              value={form.poReff?.vendorName || ''}
              options={[]}
            />
          ) : (
            <Select
              required
              fullWidth
              withSearch
              placeholder="Cari lokasi"
              displayKey="location_name"
              value={form.destination}
              options={constants.destinations}
              onChange={(value) => handleChangeForm('destination', value)}
            />
          )}
        </Fieldset>

        <Fieldset label="Email" required>
          <TextField
            fullWidth
            placeholder="nama@email.com"
            value={form.email}
            onChange={(e) => handleChangeForm('email', e.target.value)}
            error={!!errorField.email}
            errorMessage={errorField.email}
          />
        </Fieldset>

        <Fieldset label="Tanggal SO">
          <DatePicker
            dateType="single"
            value={form.soDate}
            onChange={(value) => handleChangeForm('soDate', value)}
          />
        </Fieldset>

        <Fieldset label="Remarks" required>
          <Select
            required
            fullWidth
            placeholder="Pilih remarks"
            displayKey="description"
            value={form.remarks}
            options={constants.remarks}
            onChange={(value) => handleChangeForm('remarks', value)}
          />
        </Fieldset>

        <Fieldset label="Instruksi Pengiriman" fullWidth>
          <TextField
            fullWidth
            placeholder={`Tulis instruksi pengiriman dengan detail${'\n'}Contoh: Mohon barang fluid diletakkan paling atas`}
            value={form.shippingInstructions}
            multiline
            rows={4}
            onChange={(e) => handleChangeForm('shippingInstructions', e.target.value)}
          />
        </Fieldset>
      </Box>
    </Box>
  )
}

export default withPopupConfirmation(BasicDetail)
